import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Download, MoreVertical, Trash2, Upload } from "lucide-react";
import ImportWizard from "@/components/imports/ImportWizard";
import ExportDialog from "@/components/exports/ExportDialog";
import { useCanExport } from "@/hooks/useCanExport";
import {
  useGetDncEntriesQuery,
  useAddDncEntryMutation,
  useRemoveDncEntryMutation,
} from "@/store/rest_api";
import { DncType } from "@/model/interface/DncEntry";
import { formatDateTime } from "@/util/date";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import DncCheckDialog from "./DncCheckDialog";
import EmailSuppressionList from "./EmailSuppressionList";

const TYPE_LABELS: Record<string, string> = {
  DNC: "Do Not Call",
  DNT: "Do Not Text",
  BOTH: "DNC + DNT",
  DO_NOT_AUTOCALL: "No automated calls",
  BLOCKED: "Block inbound calls",
};

const SOURCE_LABELS: Record<string, string> = {
  MANUAL: "Manual",
  OPT_OUT: "Opt-out",
  LITIGATOR: "Litigator",
  AGENT: "Agent",
  IMPORT: "Import",
};

export default function DncManagement() {
  const [channel, setChannel] = useState("phone");
  const [page, setPage] = useState(0);
  const [typeFilter, setTypeFilter] = useState<string>("ALL");
  const [newPhone, setNewPhone] = useState("");
  const [newType, setNewType] = useState<DncType>("BOTH");
  const [checkDialogOpen, setCheckDialogOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const { canExport } = useCanExport();

  const { data, isLoading } = useGetDncEntriesQuery({
    page,
    size: 50,
    type: typeFilter !== "ALL" ? (typeFilter as DncType) : undefined,
  });
  const [addEntry, { isLoading: isAdding }] = useAddDncEntryMutation();
  const [removeEntry] = useRemoveDncEntryMutation();

  const entries = data?.items;

  const handleAdd = async () => {
    const phone = newPhone.trim();
    if (!phone) {
      toast.error("Phone number is required");
      return;
    }
    try {
      await addEntry({ phoneNumberE164: phone, type: newType }).unwrap();
      toast.success("Number added to DNC list");
      setNewPhone("");
    } catch (e: any) {
      toast.error(e?.detail || "Failed to add entry");
    }
  };

  const handleRemove = async (id: number) => {
    try {
      await removeEntry(id).unwrap();
      toast.success("Entry removed");
    } catch (e: any) {
      toast.error(e?.detail || "Failed to remove entry");
    }
  };

  const handleTypeFilterChange = (value: string) => {
    setTypeFilter(value);
    setPage(0);
  };

  return (
    <div className="space-y-6 p-4">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Do Not Contact</h1>
        <p className="text-muted-foreground">
          Manage the numbers and email addresses your organisation must not contact.
        </p>
      </div>

      <Tabs value={channel} onValueChange={setChannel}>
        <TabsList>
          <TabsTrigger value="phone">Phone Numbers</TabsTrigger>
          <TabsTrigger value="email">Email Addresses</TabsTrigger>
        </TabsList>

        <TabsContent value="email" className="mt-6">
          <EmailSuppressionList />
        </TabsContent>

        <TabsContent value="phone" className="mt-6 space-y-6">
      <div className="flex items-center gap-3">
        <Input
          placeholder="+1234567890"
          value={newPhone}
          onChange={(e) => setNewPhone(e.target.value)}
          className="w-50"
          onKeyDown={(e) => e.key === "Enter" && handleAdd()}
        />
        <Select value={newType} onValueChange={(v) => setNewType(v as DncType)}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="BOTH">DNC + DNT</SelectItem>
            <SelectItem value="DNC">Do Not Call</SelectItem>
            <SelectItem value="DNT">Do Not Text</SelectItem>
            <SelectItem value="BLOCKED">Block inbound calls</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={handleAdd} disabled={isAdding}>
          Add
        </Button>
        <div className="flex-1" />
        <Button variant="outline" onClick={() => setImportOpen(true)}>
          <Upload className="mr-2 h-4 w-4" />
          Import
        </Button>
        {canExport && (
          <Button variant="outline" onClick={() => setExportOpen(true)}>
            <Download className="mr-2 h-4 w-4" />
            Export
          </Button>
        )}
        <Button variant="outline" onClick={() => setCheckDialogOpen(true)}>
          Check Number
        </Button>
      </div>

      <div className="flex items-center gap-3">
        <Select value={typeFilter} onValueChange={handleTypeFilterChange}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="All types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All types</SelectItem>
            <SelectItem value="DNC">Do Not Call</SelectItem>
            <SelectItem value="DNT">Do Not Text</SelectItem>
            <SelectItem value="BOTH">DNC + DNT</SelectItem>
            <SelectItem value="BLOCKED">Block inbound calls</SelectItem>
          </SelectContent>
        </Select>
        {data && (
          <span className="text-sm text-muted-foreground">
            {data.totalElements} entr{data.totalElements !== 1 ? "ies" : "y"}
          </span>
        )}
      </div>

      {isLoading && <Skeleton className="h-64 w-full" />}

      {!isLoading && (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Phone Number</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Added by</TableHead>
                <TableHead>Added</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries && entries.length > 0 ? (
                entries.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell className="font-mono">{entry.phoneNumberE164}</TableCell>
                    <TableCell>
                      <Badge variant={entry.type === "BOTH" ? "destructive" : "secondary"}>
                        {TYPE_LABELS[entry.type] ?? entry.type}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {SOURCE_LABELS[entry.source] ?? entry.source}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {[entry.createdByMembership?.user?.firstName, entry.createdByMembership?.user?.lastName].filter(Boolean).join(" ") || "System"}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDateTime(entry.createdAt)}
                    </TableCell>
                    <TableCell className="text-right">
                      {entry.source !== "LITIGATOR" && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8" title="Options">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem className="text-destructive" onClick={() => void handleRemove(entry.id)}>
                              <Trash2 className="mr-2 h-4 w-4" />
                              Remove from list
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground">
                    No entries in your Do Not Contact list.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {data && data.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            Page {data.page + 1} of {data.totalPages}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={data.page === 0} onClick={() => setPage((p) => p - 1)}>
              Previous
            </Button>
            <Button variant="outline" size="sm" disabled={data.last} onClick={() => setPage((p) => p + 1)}>
              Next
            </Button>
          </div>
        </div>
      )}

          <DncCheckDialog open={checkDialogOpen} onOpenChange={setCheckDialogOpen} />
          <ImportWizard open={importOpen} onClose={() => setImportOpen(false)} entityType="DNC" />
          <ExportDialog
            open={exportOpen}
            onClose={() => setExportOpen(false)}
            title="Export Do Not Contact list"
            description="Every number on the list with its type, source and the date it was added."
            request={{ kind: "DNC" }}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
